import React from 'react';
import { useFragment } from 'react-relay';
import { graphql } from 'relay-runtime';
import { Box, xcss } from '@atlaskit/primitives';
import { fg } from '@atlassian/jira-feature-gating';
import { useIntl } from '@atlassian/jira-intl';
import type { groupNotificationEmailsWrapper_personalSettingsPreferenceFields$key } from '@atlassian/jira-relay/src/__generated__/groupNotificationEmailsWrapper_personalSettingsPreferenceFields.graphql';
import { BatchWindowPreferenceField } from '../batch-window/index.tsx';
import { EnableBatchingPreferenceField } from '../enable-batching/index.tsx';
import messages from './messages.tsx';

export const GroupNotificationEmailsWrapper = ({
	groupNotificationEmailsFragment,
}: {
	groupNotificationEmailsFragment: groupNotificationEmailsWrapper_personalSettingsPreferenceFields$key;
}) => {
	const data = useFragment(
		graphql`
			fragment groupNotificationEmailsWrapper_personalSettingsPreferenceFields on JiraNotificationOptions {
				batchWindow
				...enableBatching_personalSettingsPreferenceFields_EnableBatchingPreferenceField
				...batchWindow_personalSettingsPreferenceFields_BatchWindowPreferenceField
			}
		`,
		groupNotificationEmailsFragment,
	);
	const { formatMessage } = useIntl();
	return (
		<Box xcss={containerStyles}>
			<Box
				xcss={[
					headerWrapperStyles,
					data.batchWindow === 'NO_BATCHING' ? undefined : enabledHeaderWrapperStyles,
				]}
			>
				<EnableBatchingPreferenceField enableBatchingFragment={data} />
			</Box>
			{data.batchWindow === 'NO_BATCHING' ? null : (
				<Box
					testId="personal-settings-preference-fields.common.ui.group-notification-emails-wrapper.box"
					xcss={contentWrapperStyles}
				>
					<Box xcss={preferenceRowStyles} id="select-batch-notification-duration-label">
						{formatMessage(
							fg('jira-issue-terminology-refresh-m3')
								? messages.wrapperNoteIssueTermRefresh
								: messages.wrapperNote,
						)}
					</Box>
					<BatchWindowPreferenceField batchWindowFragment={data} />
				</Box>
			)}
		</Box>
	);
};

const containerStyles = xcss({
	boxSizing: 'border-box',
	marginTop: 'space.100',
	borderRadius: 'border.radius.075',
});

const headerWrapperStyles = xcss({
	borderWidth: 'border.width',
	borderStyle: 'solid',
	borderColor: 'color.border',
	borderRadius: 'border.radius.100',
	paddingRight: 'space.150',
	paddingLeft: 'space.200',
	paddingTop: 'space.050',
	paddingBottom: 'space.050',
});

const enabledHeaderWrapperStyles = xcss({
	borderBottomLeftRadius: '0px',
	borderBottomRightRadius: '0px',
	backgroundColor: 'elevation.surface.sunken',
});

const contentWrapperStyles = xcss({
	backgroundColor: 'elevation.surface',
	borderWidth: 'border.width',
	borderStyle: 'solid',
	borderColor: 'color.border',
	borderRadius: 'border.radius.100',
	borderTop: 'none',
	borderTopLeftRadius: '0px',
	borderTopRightRadius: '0px',
	paddingRight: 'space.200',
	paddingLeft: 'space.200',
	paddingTop: 'space.150',
	paddingBottom: 'space.150',
});

const preferenceRowStyles = xcss({
	marginBottom: 'space.100',
});
